import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { MatNavList } from '@angular/material/list';
import { UserDetails } from '../models/user.model';
import { SideNavMenuComponent } from './side-nav-menu/side-nav-menu.component';
import { MenuItem } from './side-nav.models';

@Component({
    selector: 'custom-side-nav',
    templateUrl: './custom-side-nav.component.html',
    styleUrl: './custom-side-nav.component.scss',
    standalone: true,
    imports: [MatNavList, SideNavMenuComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomSideNavComponent {
    userDetails = input.required<UserDetails>();
    collapsed = model.required<boolean>();

    readonly menuItems = computed(() => this.buildMenuItems(this.userDetails()));

    private buildMenuItems(user: UserDetails): MenuItem[] {
        const isPeManager = user.activeRoleOgName === 'district-pe-manager';
        const isBlockLevel = user.activeGeoLevel === 'block';
        const isDistrictLevel = user.activeGeoLevel === 'district';
        const isStateOrNationalGeoLevel = ['state', 'national'].includes(user.activeGeoLevel);
        const isStateIncubatorAgency = user.activeRoleOgName === 'state-incubator-agency';
        const isClfBookkeeper = user.activeRoleOgName === 'clf-bookkeeper';
        const isStateOrNationalManager = [
            'state-manager-farm',
            'state-manager-nonfarm',
            'national-manager-farm',
            'national-manager-nonfarm'
        ].includes(user.activeRoleOgName);

        if (isStateIncubatorAgency) {
            return [
                { label: 'dashboard', icon: 'dashboard', route: 'dashboard' },
                { label: 'Profile Details', icon: 'profile-details', route: 'incubator/profile-details' },
                { label: 'Enterprise Shortlisting', icon: 'enterprise-shortlist', route: 'incubator/shortlisting' },
                {
                    label: 'Curriculum & Evaluation',
                    icon: 'curriculum-design',
                    subItems: [
                        { label: 'curriculumDesign', route: 'incubator/curriculum-design' },
                        { label: 'performanceEvaluation', route: 'incubator/performance-evaluation' }
                    ]
                },
                {
                    label: 'sideNavChallengeFund',
                    icon: 'challengefund-icon',
                    subItems: [
                        { label: 'challengeFundDesign', route: 'challenge-fund/design' },
                        { label: 'performanceEvaluation', route: 'challenge-fund/performance-evaluation' }
                    ]
                }
            ];
        }

        if (isClfBookkeeper) {
            return [
                { label: 'dashboard', icon: 'dashboard', route: 'dashboard' },
                { label: 'sideNavPMFME', icon: 'pmfme', route: 'pmfme-nodal' }
            ];
        }

        if (isPeManager) {
            return [
                { label: 'dashboard', icon: 'dashboard', route: 'dashboard' },
                {
                    label: 'sideNavProducerEnterprise',
                    icon: 'producer-enterprise',
                    subItems: [
                        { label: 'profiles', route: 'producer-enterprise/producer-enterprise-profiles-list' },
                        { label: 'quarterlyTransactions', route: 'producer-enterprise/quarterly-transactions' },
                        { label: 'legalCompliances', route: 'producer-enterprise/legal-compliances' }
                    ]
                }
            ];
        }

        return [
            { label: 'dashboard', icon: 'dashboard', route: 'dashboard' },
            // {
            //     label: 'livelihoodProfile',
            //     icon: 'livelihood-sidemenu-icon',
            //     route: 'livelihood'
            // },
            {
                label: 'sideNavProducerGroup',
                icon: 'producer-group',
                subItems: [
                    { label: 'profile', route: 'profile/pg-list' },
                    { label: 'activity', route: 'activity/list' },
                    { label: 'businessPlan', route: 'business-plan/recent-plan' },
                    { label: 'profitSettlement', route: 'profit-settlement/profit-settlement-pg-list' },
                    { label: 'incomeExpense', route: 'income-expense/income-expense-list' },
                    { label: 'pgFund', route: 'pg-fund/pg-fund-list' }
                ]
            },

            ...(isBlockLevel
                ? [
                      {
                          label: 'userMapping',
                          icon: 'user-mapping',
                          subItems: [
                              { label: 'producerGroup', route: 'user-mapping/pg-mapping-list' },
                              { label: 'selfHelpGroup', route: 'user-mapping/shg-mapping-list' }
                          ]
                      }
                  ]
                : []),

            ...(!isBlockLevel
                ? [
                      {
                          label: 'sideNavProducerEnterprise',
                          icon: 'producer-enterprise',
                          subItems: [
                              { label: 'profiles', route: 'producer-enterprise/producer-enterprise-profiles-list' },
                              {
                                  label: 'quarterlyTransactions',
                                  route: 'producer-enterprise/quarterly-transaction-list'
                              },
                              { label: 'legalCompliances', route: 'producer-enterprise/legal-compliances' }
                          ]
                      }
                  ]
                : []),
            {
                label: 'sideNavClusterManagement',
                icon: 'ifc',
                subItems: [
                    { label: 'profiles', route: 'ifc-clusters/profiles' },
                    { label: 'activity', route: 'ifc-clusters/activity' },
                    { label: 'services', route: 'ifc-clusters/services' },
                    { label: 'lsc', route: 'ifc-clusters/lsc' }
                    // {
                    //     label: 'businessPlan',
                    //     route: 'business-plan/recent-plan'
                    // }
                ]
            },
            {
                label: 'sideNavCHC',
                icon: 'chc-admin',
                subItems: [
                    { label: 'profiles', route: 'chc-admin/profiles' },
                    { label: 'bookings', route: 'chc-admin/bookings' },
                    { label: 'toolMaintenance', route: 'chc-admin/tool-maintenance' },
                    { label: 'expense', route: 'chc-admin/expense' }
                ]
            },
            {
                label: 'sideNavEntrepreneurEnterprise',
                icon: 'eep-side-nav',
                subItems: [
                    { label: 'individualEnterprise', route: 'entrepreneur-enterprise-profile/individual' },
                    { label: 'groupEnterprise', route: 'entrepreneur-enterprise-profile/group' }
                ]
            },
            ...(isStateOrNationalManager
                ? [
                      {
                          label: 'sideNavIncubatorProgram',
                          icon: 'incubator-program',
                          subItems: [
                              { label: 'incubatorOnboarding', route: 'incubator/list' },
                              { label: 'enterpriseShortlisting', route: 'incubator/shortlisting-list' },
                              {
                                  label: 'curriculumDetailsEvaluation',
                                  route: 'incubator/curriculum-list'
                              },
                              {
                                  label: 'Challenge Fund & Fund Tracking',
                                  route: 'incubator/challenge-fund-list'
                              }
                          ]
                      }
                  ]
                : []),
            ...(isStateIncubatorAgency
                ? [
                      {
                          label: 'sideNavChallengeFund',
                          icon: 'challengefund-icon',
                          subItems: [
                              {
                                  label: 'challengeFundDesign',
                                  route: 'challenge-fund/design'
                              },
                              {
                                  label: 'performanceEvaluation',
                                  route: 'challenge-fund/performance-evaluation'
                              }
                          ]
                      }
                  ]
                : []),
            {
                label: 'sideNavGrameenExpress',
                icon: 'agey-icon',
                subItems: [{ label: 'proposals', route: 'agey/proposals' }]
            },
            {
                label: 'sideNavPMFME',
                icon: 'pmfme',
                route: 'pmfme-block-manager'
            },
            {
                label: 'sideNavMasterManagement',
                icon: 'master-icon',
                subItems: [
                    { label: 'assets', route: 'master/assets' },
                    { label: 'equityLiabilities', route: 'master/equity-liabilities' },
                    { label: 'clusterType', route: 'master/cluster-type' }
                ]
            },
            {
                label: 'sideNavReportManagement',
                icon: 'report-side-nav',
                subItems: [
                    { label: 'pgSummary', route: 'reports/pg-summary' },
                    ...(!isBlockLevel && !isDistrictLevel ? [{ label: 'peSummary', route: 'reports/pe-summary' }] : []),
                    { label: 'ifcSummary', route: 'reports/cluster-summary' },
                    // { label: 'ifcSummaryMeta', route: 'expenditure-status-meta/35' },
                    { label: 'chcSummary', route: 'reports/chc-summary' },
                    { label: 'eepSummary', route: 'reports/eep-summary' },
                    ...(isStateOrNationalGeoLevel
                        ? [{ label: 'incubatorReport', route: 'reports/incubator-summary' }]
                        : [])
                ]
            }
        ];
    }
}
